import axios from "axios";
import { Notify, Toast } from 'vant';
import { getToken } from "@/utils/auth";
import errorCode from "@/utils/errorCode";

axios.defaults.headers["Content-Type"] = "application/json;charset=utf-8";

// 存储待处理请求的 AbortController，用于页面切换时取消请求
const pendingRequests = new Map();

// 生成请求唯一标识
function getRequestKey(config) {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join("&");
}

// 添加待处理请求
function addPending(config) {
  const requestKey = getRequestKey(config);
  if (pendingRequests.has(requestKey)) {
    // 相同请求已存在，取消旧的
    const controller = pendingRequests.get(requestKey);
    controller.abort();
  }
  const controller = new AbortController();
  config.signal = controller.signal;
  pendingRequests.set(requestKey, controller);
}

// 移除待处理请求
function removePending(config) {
  const requestKey = getRequestKey(config);
  pendingRequests.delete(requestKey);
}

// 取消所有待处理请求（路由切换时调用）
export function cancelAllPending() {
  pendingRequests.forEach((controller) => {
    controller.abort();
  });
  pendingRequests.clear();
}

// 创建axios实例
const service = axios.create({
  baseURL: process.env.BASE_API,
  timeout: 30000
});

// 判断是否为可重试的错误（超时或网络错误）
function isRetryableError(error) {
  const msg = (error && error.message) || "";
  return msg.includes("timeout") || msg === "Network Error" || msg.includes("Network");
}

// 带指数退避的重试逻辑
// requestFn: 返回 Promise 的工厂函数，每次重试时重新调用
async function requestWithRetry(requestFn, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const result = await requestFn();
      return result;
    } catch (error) {
      // 最后一次重试或不可重试错误，直接抛出
      if (i >= retries || !isRetryableError(error)) {
        throw error;
      }
      // 指数退避：1s, 2s, 4s
      const delay = Math.pow(2, i) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

// request拦截器
service.interceptors.request.use(
  config => {
    addPending(config);

    if (getToken()) {
      config.headers["Authorization"] = "Bearer " + getToken();
    }
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    // get请求映射params参数
    if (config.method === "get" && config.params) {
      let url = config.url + "?";
      for (const propName of Object.keys(config.params)) {
        const value = config.params[propName];
        var part = encodeURIComponent(propName) + "=";
        if (value !== null && typeof value !== "undefined") {
          if (typeof value === "object") {
            for (const key of Object.keys(value)) {
              let params = propName + "[" + key + "]";
              var subPart = encodeURIComponent(params) + "=";
              url += subPart + encodeURIComponent(value[key]) + "&";
            }
          } else {
            url += part + encodeURIComponent(value) + "&";
          }
        }
      }
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  res => {
    // 请求完成，移除待处理标记
    removePending(res.config);

    const code = res.data.code || 20000;
    const msg = errorCode[code] || res.data.msg || errorCode["default"];
    if (code === 401) {
      // Token过期，清除token并跳转首页
      import("@/utils/auth").then(({ removeToken }) => {
        removeToken();
        location.reload();
      });
    } else if (code === 500) {
      Notify({
        message: msg,
        type: "danger"
      });
      return Promise.reject(new Error(msg));
    } else if (code !== 20000) {
      Toast.fail(msg);
      return Promise.reject("error");
    } else {
      return res.data;
    }
  },
  error => {
    // 请求完成（即使失败），移除待处理标记
    if (error.config) {
      removePending(error.config);
    }

    // 如果是主动取消的请求，打上标记后抛出，不显示错误提示
    if (axios.isCancel(error)) {
      error._canceled = true;
      return Promise.reject(error);
    }

    let { message } = error;
    if (message == "Network Error") {
      message = "后端接口连接异常";
    } else if (message.includes("timeout")) {
      message = "系统接口请求超时";
    }
    Notify({
      message: message,
      type: "danger",
      duration: 5 * 1000
    });
    return Promise.reject(error);
  }
);

// 导出一个包装函数，自动带重试（仅对幂等的 GET 请求重试）
// 拦截直接调用 request(config) 和 request.get/post/put/delete 两种方式
const serviceWithRetry = new Proxy(service, {
  apply(target, thisArg, args) {
    var config = args[0];
    var method = (config && config.method || "get").toLowerCase();
    if (method === "get") {
      return requestWithRetry(function() {
        return target.apply(thisArg, [config]);
      });
    }
    return target.apply(thisArg, args);
  },
  get(target, prop) {
    var original = target[prop];
    if (typeof original === "function" && prop === "get") {
      return function() {
        var args = arguments;
        return requestWithRetry(function() {
          return original.apply(target, args);
        });
      };
    }
    return original;
  }
});

export default serviceWithRetry;
export { service as rawService };



// WEBPACK FOOTER //
// ./src/utils/request.js